/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Workflow Detail — header + dynamic inputs form + recent runs.
 *
 * Header explains the workflow (persona pool, stages, tools, pricing).
 * Left pane renders the inputs schema and starts the run. Right pane
 * shows the 5 most recent runs and links into the Run Viewer.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import workflowAdminApi, {
  extractWorkflow,
  extractRuns,
} from '../../services/workflowAdminApi';
import DynamicInputsForm from './components/DynamicInputsForm';
import DemoModeToggle from '../../components/workflows/DemoModeToggle';
import WorkflowDemoChrome from '../../components/workflows/WorkflowDemoChrome';
import { displayCost, displayLatency, displayRunId, isDemoMode } from '../../utils/demoMode';
import './WorkflowsCatalog.css';
import './WorkflowDetail.css';

function classifyError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status === 404) return { kind: 'not_found', message: 'Workflow not found.' };
  if (status === 503) return { kind: 'unavailable', message: 'Workflow Factory is temporarily unavailable.' };
  if (status === 403) return { kind: 'permission', message: body?.error || 'You do not have permission.' };
  if (status === 401) return { kind: 'auth', message: 'Session expired. Please sign in again.' };
  return { kind: 'server', message: body?.error || err?.message || 'Failed to load workflow.' };
}

function runStateClass(state) {
  const s = String(state || '').toLowerCase();
  if (s === 'completed') return 'wf-run-state wf-run-state-completed';
  if (s === 'failed') return 'wf-run-state wf-run-state-failed';
  if (s === 'running') return 'wf-run-state wf-run-state-running';
  if (s === 'awaiting_confirmation') return 'wf-run-state wf-run-state-pending';
  return 'wf-run-state';
}

function formatRunTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

function PersonaPool({ workflow, demo }) {
  const personas = Array.isArray(workflow.personas)
    ? workflow.personas
    : (workflow.persona_pool || []);
  if (!personas.length) return null;
  return (
    <div className="wf-detail-personas">
      <span className="wf-detail-label">Persona pool</span>
      <div className="wf-detail-persona-chips">
        {personas.map((p, idx) => {
          const id = typeof p === 'string' ? p : (p.id || p.persona_id);
          const name = typeof p === 'string' ? p
            : (p.display_name || p.name || p.id || p.persona_id);
          return (
            <span key={id || idx} className="wf-detail-chip wf-detail-chip-persona">
              {demo ? '◐' : '👤'} {name}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function StagesTimeline({ workflow }) {
  const stages = Array.isArray(workflow.stages) ? workflow.stages : [];
  if (!stages.length) return null;
  return (
    <div className="wf-detail-stages">
      <span className="wf-detail-label">Stages</span>
      <ol className="wf-detail-stage-track">
        {stages.map((s, idx) => {
          const id = s.id || s.stage_id || s.name || `stage-${idx}`;
          const name = s.name || s.id || s.stage_id;
          const archetype = s.archetype || s.archetype_id;
          return (
            <li key={id} className="wf-detail-stage-chip">
              <span className="wf-detail-stage-idx">{idx + 1}</span>
              <span className="wf-detail-stage-name">{name}</span>
              {archetype && (
                <span className="wf-detail-stage-archetype">{archetype}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ToolsCloud({ workflow }) {
  const tools = Array.isArray(workflow.tools)
    ? workflow.tools
    : (workflow.tool_pool || []);
  if (!tools.length) return null;
  return (
    <div className="wf-detail-tools">
      <span className="wf-detail-label">Tools available</span>
      <div className="wf-detail-tool-chips">
        {tools.map((t, idx) => {
          const id = typeof t === 'string' ? t : (t.id || t.tool_id || t.name);
          return (
            <span key={id || idx} className="wf-detail-chip wf-detail-chip-tool">{id}</span>
          );
        })}
      </div>
    </div>
  );
}

function defaultValuesForSchema(schema) {
  const root = schema?.properties ? schema : (schema?.inputs_schema || schema || {});
  const props = root.properties || {};
  const out = {};
  for (const [name, field] of Object.entries(props)) {
    if (field?.default !== undefined) out[name] = field.default;
    else if (field?.type === 'boolean') out[name] = false;
    // Object/array fields stay UNSET — '' is invalid JSON and would ship a
    // bad payload that the V2 engine rejects. ObjectField (DynamicInputsForm)
    // pushes a parsed value to state once the user types valid JSON; until
    // then the required-gate naturally treats it as not-yet-provided.
    else if (field?.type === 'object' || field?.type === 'array') continue;
    else out[name] = '';
  }
  return out;
}

export default function WorkflowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [workflow, setWorkflow] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputs, setInputs] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const demo = isDemoMode();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [wfBody, runsBody] = await Promise.all([
        workflowAdminApi.getWorkflow(id),
        workflowAdminApi.listRecentRuns(id, { limit: 5 }).catch(() => ({ runs: [] })),
      ]);
      const wf = extractWorkflow(wfBody);
      if (!wf) {
        setError({ kind: 'not_found', message: 'Workflow not found.' });
        return;
      }
      setWorkflow(wf);
      setRecentRuns(extractRuns(runsBody));
      setInputs(defaultValuesForSchema(wf.inputs_schema || wf.schema));
    } catch (err) {
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleInputChange = (name, value) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = await workflowAdminApi.startRun(id, inputs);
      const runId = body?.run_id
        || body?.data?.run_id
        || body?.id
        || body?.data?.id;
      if (!runId) {
        setSubmitError('V2 did not return a run id.');
        return;
      }
      navigate(`/admin/workflow-runs/${runId}`);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error || err?.message || 'Failed to start run.';
      setSubmitError(status ? `${status} — ${message}` : message);
    } finally {
      setSubmitting(false);
    }
  }, [id, inputs, navigate]);

  // Auto-focus the inputs form when arriving via #start hash (from the
  // catalog "Start Run" button).
  useEffect(() => {
    if (window.location.hash !== '#start') return;
    const el = document.querySelector('.wf-detail-form-pane');
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [workflow]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="wf-detail-page">
          <Link to="/admin/workflows" className="wf-detail-back">← Catalog</Link>
          <div className="wf-card wf-card-skeleton" style={{ minHeight: 360, marginTop: 16 }} />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="wf-detail-page">
          <Link to="/admin/workflows" className="wf-detail-back">← Catalog</Link>
          <div className={`wf-catalog-state wf-catalog-state-${error.kind}`}>
            <h2>Can't open this workflow</h2>
            <p>{error.message}</p>
            <button type="button" className="wf-btn wf-btn-secondary" onClick={load}>
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // V2's `pricing` may be a string label OR an object {model, amount_usd}.
  // displayCost handles the object case so it never reaches JSX (React #31).
  const pricing = workflow.pricing_display
    || displayCost(workflow.pricing, demo)
    || (workflow.price_usd ? `$${workflow.price_usd.toLocaleString()} USD` : null);

  return (
    <AdminLayout>
      <div className="wf-detail-page">
        <div className="wf-detail-topbar">
          <Link to="/admin/workflows" className="wf-detail-back">← Catalog</Link>
          <DemoModeToggle />
        </div>

        <header className="wf-detail-header">
          <h1 className="wf-detail-title">
            {workflow.name || workflow.display_name || workflow.id}
          </h1>
          <div className="wf-detail-meta">
            {workflow.domain && (
              <span className={`wf-card-domain wf-card-domain-${String(workflow.domain).toLowerCase()}`}>
                {workflow.domain}
              </span>
            )}
            {pricing && !demo && (
              <span className="wf-detail-pricing">{pricing}</span>
            )}
            {!demo && workflow.state && (
              <span className={`wf-card-state wf-card-state-${workflow.state}`}>
                {workflow.state}
              </span>
            )}
          </div>
          {workflow.description && (
            <p className="wf-detail-description">{workflow.description}</p>
          )}

          <PersonaPool workflow={workflow} demo={demo} />
          <StagesTimeline workflow={workflow} />
          <ToolsCloud workflow={workflow} />
        </header>

        <div className="wf-detail-body">
          <section className="wf-detail-form-pane">
            <h2 className="wf-detail-pane-title">Start a run</h2>
            <DynamicInputsForm
              schema={workflow.inputs_schema || workflow.schema}
              values={inputs}
              onChange={handleInputChange}
              onSubmit={handleSubmit}
              submitting={submitting}
              submitError={submitError}
            />
          </section>

          <aside className="wf-detail-runs-pane">
            <h2 className="wf-detail-pane-title">Recent runs</h2>
            {recentRuns.length === 0 ? (
              <p className="wf-detail-runs-empty">
                No runs yet. Start one to see it here.
              </p>
            ) : (
              <ul className="wf-detail-runs-list">
                {recentRuns.map((r) => {
                  const runId = r.run_id || r.id;
                  const state = r.state || r.status;
                  const latency = r.total_latency_ms ?? r.latency_ms;
                  return (
                    <li key={runId} className="wf-detail-run-row">
                      <Link to={`/admin/workflow-runs/${runId}`} className="wf-detail-run-link">
                        <div className="wf-detail-run-id">{displayRunId(runId, demo)}</div>
                        <div className="wf-detail-run-meta">
                          <span className={runStateClass(state)}>{state || 'unknown'}</span>
                          <span className="wf-detail-run-latency">
                            {displayLatency(latency, demo)}
                          </span>
                        </div>
                        <div className="wf-detail-run-timestamp">
                          {formatRunTimestamp(r.started_at || r.created_at)}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </div>

        <WorkflowDemoChrome />
      </div>
    </AdminLayout>
  );
}
